import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useExperience } from '../../context/ExperienceContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { Button } from '../ui/Button';
import { WizardHeader } from './wizard/WizardHeader';
import { WizardProgress } from './wizard/WizardProgress';
import { WizardStepLabel } from './wizard/WizardStepLabel';
import { UkiyoWaveDivider } from './wizard/WizardDividers';
import { IdentityStep } from './wizard/steps/IdentityStep';
import { InterestsStep } from './wizard/steps/InterestsStep';
import { DarumaBubble, type DarumaPose } from '../mascot/DarumaBubble';

type TravellerType = 'casual' | 'enthusiast';

const STEP_KEYS = [
  'onboarding.stepWelcome',
  'onboarding.stepInterests',
];

const TOTAL_STEPS = STEP_KEYS.length;

export function OnboardingWizard() {
  const { t } = useTranslation('pages');
  const { user } = useAuth();
  const { isManga, isUkiyo } = useExperience();
  const { state, setPhase, updateState } = useOnboarding();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [selectedType, setSelectedType] = useState<TravellerType>(state.isCasual ? 'casual' : 'enthusiast');
  const [interests, setInterests] = useState<string[]>(state.interests);

  const toggleInterest = useCallback((id: string) => {
    setInterests(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);

  const canContinue = (): boolean => {
    switch (step) {
      case 0: return true;
      case 1: return interests.length > 0;
      default: return false;
    }
  };

  const savePreferences = useCallback(async () => {
    if (!user) return;
    const gamificationUnlocked = selectedType === 'enthusiast';
    await supabase
      .from('user_preferences')
      .upsert({
        user_id: user.id,
        traveller_type: selectedType,
        gamification_unlocked: gamificationUnlocked,
        interests,
      }, { onConflict: 'user_id' });
  }, [user, selectedType, interests]);

  const handleNext = async () => {
    if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
      return;
    }
    if (saving || !user) return;
    setSaving(true);
    try {
      await savePreferences();
      updateState({
        isCasual: selectedType === 'casual',
        interests,
      });
      setPhase('app-tour');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const handleSkipOnboarding = useCallback(async () => {
    if (!user) return;
    try {
      await supabase.from('user_preferences').upsert({
        user_id: user.id,
        onboarding_completed: true,
      }, { onConflict: 'user_id' });
    } catch { /* non-fatal */ }
    try { localStorage.setItem('tg_daruma_app_tour_done', '1'); } catch { /* ignore */ }
    setPhase('complete');
  }, [user, setPhase]);

  if (state.phase !== 'welcome' || !user) return null;

  const cardStyle: React.CSSProperties | undefined = isManga
    ? { borderRadius: 0, border: '3px solid #0a0a0a', boxShadow: '6px 6px 0 rgba(0,0,0,0.22)' }
    : isUkiyo
      ? { borderRadius: 6, border: '1px solid rgba(27,79,138,0.3)', boxShadow: '4px 6px 0 rgba(10,40,90,0.12)' }
      : undefined;

  const footerBtnStyle: React.CSSProperties | undefined = isManga
    ? {
        background: '#0a0a0a', color: '#fff', border: '2px solid #0a0a0a', borderRadius: 0,
        fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900, letterSpacing: '0.06em',
        boxShadow: '4px 4px 0 rgba(0,0,0,0.20)',
      }
    : isUkiyo
      ? {
          background: '#0D3468', color: '#fff', borderRadius: 4,
          fontFamily: "'Noto Serif JP', serif", letterSpacing: '0.04em',
        }
      : undefined;

  const darumaForStep = (): { pose: DarumaPose; message: string } => {
    switch (step) {
      case 0: {
        if (selectedType === 'casual') return { pose: 'wave', message: t('onboarding.darumaCasualChoice') };
        if (selectedType === 'enthusiast') return { pose: 'wave', message: t('onboarding.darumaEnthusiastChoice') };
        return { pose: 'wave', message: t('onboarding.darumaWelcome') };
      }
      case 1:
        return { pose: 'point', message: t('onboarding.darumaInterests') };
      default:
        return { pose: 'wave', message: t('onboarding.darumaWelcome') };
    }
  };

  const daruma = darumaForStep();

  return (
    <div className="fixed inset-0 bg-black/60 z-[9000] flex items-center justify-center p-4">
      <div
        className={`relative bg-white dark:bg-gray-800 shadow-warm-xl max-w-2xl w-full flex flex-col max-h-[90vh] ${
          !isManga && !isUkiyo ? 'rounded-2xl' : ''
        }`}
        style={cardStyle}
      >
        <button
          onClick={handleSkipOnboarding}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
          aria-label={t('onboarding.skipOnboarding')}
          title={t('onboarding.skipOnboardingTooltip')}
        >
          <X size={20} />
        </button>
        <div className="shrink-0">
          <WizardHeader
            step={step}
            isManga={isManga}
            isUkiyo={isUkiyo}
            mangaTitles={[t('onboarding.wizardTitleWelcome').toUpperCase(), t('onboarding.wizardTitleInterests').toUpperCase()]}
            ukiyoTitles={[t('onboarding.wizardTitleWelcome'), t('onboarding.wizardTitleInterests')]}
            totalSteps={TOTAL_STEPS}
          />
        </div>
        <div className="shrink-0">
          <WizardProgress step={step} isManga={isManga} isUkiyo={isUkiyo} totalSteps={TOTAL_STEPS} />
        </div>
        <div className="overflow-y-auto flex-1 min-h-0">
          <div className="px-6 py-5">
            <UkiyoWaveDivider isUkiyo={isUkiyo} />

            <WizardStepLabel
              step={step}
              isUkiyo={isUkiyo}
              standardLabels={[t('onboarding.wizardTitleWelcome'), t('onboarding.wizardTitleInterests')]}
              ukiyoLabels={[t('onboarding.wizardLabelWhoAreYou'), t('onboarding.wizardTitleInterests')]}
            />

            <h3 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-4">
              {t(STEP_KEYS[step])}
            </h3>

            <div className="mb-4">
              <DarumaBubble pose={daruma.pose} message={daruma.message} size="md" />
            </div>

            {step === 0 && (
              <IdentityStep
                selectedType={selectedType}
                onSelect={setSelectedType}
                onTourFirst={() => {
                  updateState({ returnToWelcome: true });
                  setPhase('app-tour');
                }}
                isManga={isManga}
                isUkiyo={isUkiyo}
              />
            )}
            {step === 1 && (
              <InterestsStep
                selected={interests}
                onToggle={toggleInterest}
                isManga={isManga}
                isUkiyo={isUkiyo}
              />
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3 shrink-0 flex-wrap">
          <button
            type="button"
            onClick={handleSkipOnboarding}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
          >
            {t('onboarding.skipOnboarding')}
          </button>

          {step > 0 && (
            isManga || isUkiyo ? (
              <button
                type="button"
                onClick={handleBack}
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                <ChevronLeft size={16} className="inline -mt-0.5" /> {t('common:common.back')}
              </button>
            ) : (
              <Button variant="outline" onClick={handleBack} leftIcon={<ChevronLeft size={16} />}>
                {t('common:common.back')}
              </Button>
            )
          )}

          <span className="text-xs text-gray-500 dark:text-gray-500 hidden sm:inline">
            {step === 1 && t('onboarding.selectInterests')}
          </span>

          <div className="ml-auto">
            {isManga || isUkiyo ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canContinue() || saving}
                className="px-5 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                style={footerBtnStyle}
              >
                {t('onboarding.continue')} <ChevronRight size={16} className="inline -mt-0.5 ml-1" />
              </button>
            ) : (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!canContinue() || saving}
                rightIcon={<ChevronRight size={16} />}
              >
                {t('onboarding.continue')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
